import { cn, tw } from '../../../utils/classnames.js';

const lightBoxClassName = tw`fill-black/[12%]`;
const lightStrokeClassName = tw`stroke-black/6`;

const darkBoxClassName = tw`fill-white/6`;
const darkStrokeClassName = tw`stroke-white/6`;

const transitionClassName = tw`transition-all duration-500`;

type WireframeAppProps = {
  variant: 'light' | 'dark';
  detailed?: boolean;
  className?: string;
};

function WireframeApp({ variant, detailed, className }: WireframeAppProps) {
  const isLight = variant === 'light';
  const isDark = variant === 'dark';

  return (
    <svg
      width="93"
      height="130"
      viewBox="0 0 93 130"
      fill="none"
      className={className}
    >
      {/* bg */}
      <rect
        x="0"
        y="0"
        width="93"
        height="130"
        rx="16"
        className={cn(
          isLight && 'fill-[url(#gradient-bg-base)]',
          isDark && 'fill-[#25282d]',
        )}
      />
      <rect
        x="0"
        y="0"
        width="93"
        height="130"
        rx="16"
        className={cn(
          isLight && 'fill-[url(#gradient-bg-colors)]',
          isDark && 'fill-[#25282d]',
        )}
      />
      <rect
        x="0.5"
        y="0.5"
        width="92"
        height="129"
        rx="15.5"
        className={cn(isLight && 'stroke-black/6', isDark && 'stroke-white/6')}
      />

      {/* title bar */}
      <rect
        x="39"
        y="6"
        width="16"
        height="2.84375"
        rx="1.42188"
        className={cn(
          transitionClassName,
          isLight && 'fill-black/[11%]',
          isDark && 'fill-white/6',
          detailed && 'w-[25px] -translate-x-[5px]',
        )}
      />

      <g
        className={cn(
          transitionClassName,
          !detailed && 'translate-x-[5px] opacity-0',
        )}
      >
        <circle cx="31.5" cy="7.5" r="1.5" fill="#e3afbe" />
        <circle
          cx="31.5"
          cy="7.5"
          r="1.4253"
          strokeWidth="0.149405"
          className={cn(
            isLight && 'stroke-black/6',
            isDark && 'stroke-white/6',
          )}
        />
      </g>

      <g className={cn(transitionClassName, !detailed && 'opacity-0')}>
        <circle
          cx="14.5"
          cy="7.5"
          r="1.5"
          className={cn(isLight && 'fill-black/55', isDark && 'fill-white/55')}
        />
        <circle
          cx="14.5"
          cy="7.5"
          r="1.4253"
          strokeWidth="0.149405"
          className={cn(
            isLight && 'stroke-black/6',
            isDark && 'stroke-white/6',
          )}
        />

        <circle
          cx="18.5"
          cy="7.5"
          r="1.5"
          className={cn(isLight && 'fill-black/35', isDark && 'fill-white/35')}
        />
        <circle
          cx="18.5"
          cy="7.5"
          r="1.4253"
          strokeWidth="0.149405"
          className={cn(
            isLight && 'stroke-black/6',
            isDark && 'stroke-white/6',
          )}
        />

        <circle
          cx="22.5"
          cy="7.5"
          r="1.5"
          className={cn(isLight && 'fill-black/15', isDark && 'fill-white/15')}
        />
        <circle
          cx="22.5"
          cy="7.5"
          r="1.4253"
          strokeWidth="0.149405"
          className={cn(
            isLight && 'stroke-black/6',
            isDark && 'stroke-white/6',
          )}
        />
      </g>

      {/* balance */}
      <rect
        x="27"
        y="24"
        width="40"
        height="10"
        rx="3"
        className={cn(
          transitionClassName,
          isLight && lightBoxClassName,
          isDark && darkBoxClassName,
          detailed && 'w-[28px] translate-x-[6px]',
        )}
      />
      <rect
        x="27"
        y="28"
        width="5"
        height="6"
        rx="2"
        className={cn(
          transitionClassName,
          isLight && lightBoxClassName,
          isDark && darkBoxClassName,
          !detailed && 'h-0 translate-y-[8px] opacity-0',
        )}
      />
      <rect
        x="62"
        y="28"
        width="5"
        height="6"
        rx="2"
        className={cn(
          transitionClassName,
          isLight && lightBoxClassName,
          isDark && darkBoxClassName,
          !detailed && 'h-0 translate-y-[8px] opacity-0',
        )}
      />

      {/* head pills */}
      <g className={cn(transitionClassName, detailed && 'opacity-0')}>
        <rect
          x="28"
          y="46"
          width="37.5"
          height="6"
          rx="3"
          className={cn(
            isLight && lightBoxClassName,
            isDark && darkBoxClassName,
          )}
        />
        <rect
          x="28.079"
          y="46.079"
          width="38.3419"
          height="5.84194"
          rx="2.92097"
          strokeWidth="0.158065"
          className={cn(
            isLight && lightStrokeClassName,
            isDark && darkStrokeClassName,
          )}
        />
      </g>

      {/* head pills detailed */}
      <g className={cn(transitionClassName, !detailed && 'opacity-0')}>
        <rect
          x="28"
          y="46"
          width="18"
          height="6"
          rx="3"
          className={cn(
            isLight && lightBoxClassName,
            isDark && darkBoxClassName,
          )}
        />
        <rect
          x="28.079"
          y="46.079"
          width="17.8419"
          height="5.84194"
          rx="2.92097"
          strokeWidth="0.158065"
          className={cn(
            isLight && lightStrokeClassName,
            isDark && darkStrokeClassName,
          )}
        />

        <rect
          x="47.5"
          y="46"
          width="18"
          height="6"
          rx="3"
          className={cn(
            isLight && lightBoxClassName,
            isDark && darkBoxClassName,
          )}
        />
        <rect
          x="47.579"
          y="46.079"
          width="17.8419"
          height="5.84194"
          rx="2.92097"
          strokeWidth="0.158065"
          className={cn(
            isLight && lightStrokeClassName,
            isDark && darkStrokeClassName,
          )}
        />
      </g>

      {/* content box */}
      <rect
        x="8"
        y="62"
        width="77"
        height="34"
        rx="6"
        className={cn(
          transitionClassName,
          isLight && lightBoxClassName,
          isDark && darkBoxClassName,
          detailed && 'opacity-0',
        )}
      />

      {/* detailed content */}
      <g
        className={cn(
          transitionClassName,
          !detailed && 'translate-y-1 opacity-0',
          detailed && 'translate-y-0 opacity-100',
        )}
      >
        <g
          className={cn(
            isLight && 'fill-black/[13%]',
            isDark && 'fill-white/[13%]',
          )}
        >
          <rect x="23" y="62" width="15" height="3" rx="1.5" />
          <rect x="23" y="67" width="8" height="3" rx="1.5" />
          <rect x="65" y="62" width="15" height="3" rx="1.5" />
          <rect x="72" y="67" width="8" height="3" rx="1.5" />

          <rect x="23" y="75" width="20" height="3" rx="1.5" />
          <rect x="23" y="80" width="5" height="3" rx="1.5" />
          <rect x="72" y="75" width="8" height="3" rx="1.5" />
          <rect x="75" y="80" width="5" height="3" rx="1.5" />

          <rect x="23" y="88" width="15" height="3" rx="1.5" />
          <rect x="23" y="93" width="5" height="3" rx="1.5" />
          <rect x="65" y="88" width="15" height="3" rx="1.5" />
          <rect x="70" y="93" width="10" height="3" rx="1.5" />
        </g>

        <rect
          x="13"
          y="62"
          width="8"
          height="8"
          rx="2"
          fill="#ffa774"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />
        <rect
          x="13.1"
          y="62.1"
          width="7.8"
          height="7.8"
          rx="1.9"
          stroke="url(#gradient-stroke)"
          strokeOpacity="0.1"
          strokeWidth="0.2"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />

        <rect
          x="13"
          y="75"
          width="8"
          height="8"
          rx="2"
          fill="#7db6fa"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />
        <rect
          x="13.1"
          y="75.1"
          width="7.8"
          height="7.8"
          rx="1.9"
          stroke="url(#gradient-stroke)"
          strokeOpacity="0.1"
          strokeWidth="0.2"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />

        <rect
          x="13"
          y="88"
          width="8"
          height="8"
          rx="2"
          fill="#72eded"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />
        <rect
          x="13.1"
          y="88.1"
          width="7.8"
          height="7.8"
          rx="1.9"
          stroke="url(#gradient-stroke)"
          strokeOpacity="0.1"
          strokeWidth="0.2"
          className={cn(
            transitionClassName,
            !detailed && 'h-0 translate-y-[8px]',
          )}
        />
      </g>

      {/* navbar */}
      <rect
        x="24"
        y="108"
        width="45"
        height="16"
        rx="4"
        className={cn(
          'transition-colors duration-500',
          isLight && lightBoxClassName,
          isDark && darkBoxClassName,
          detailed && isLight && 'fill-black',
          detailed && isDark && 'fill-neutral-700',
        )}
      />

      <defs>
        <linearGradient
          id="gradient-bg-base"
          x1="58.5"
          y1="1"
          x2="58.5"
          y2="131"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.621939" stopColor="white" />
          <stop offset="0.909964" stopColor="#f5f5f5" />
        </linearGradient>
        <linearGradient
          id="gradient-bg-colors"
          x1="102.21"
          y1="1"
          x2="44.5"
          y2="131"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.09001" stopColor="#d3dd6c" stopOpacity="0.35" />
          <stop offset="0.260172" stopColor="#d3dd6c" stopOpacity="0.05" />
          <stop offset="0.509629" stopColor="#d6c7a2" stopOpacity="0.05" />
          <stop offset="0.784365" stopColor="#d9b1d8" stopOpacity="0.05" />
          <stop offset="1" stopColor="#d9b1d8" stopOpacity="0.65" />
        </linearGradient>
        <linearGradient
          id="gradient-stroke"
          x1="59"
          y1="20"
          x2="59"
          y2="40"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#181818" stopOpacity="0" />
          <stop offset="1" stopColor="#181818" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default WireframeApp;
